import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { BikeIcon, Search, Filter } from "lucide-react";
import { getActiveRentals } from "@/integrations/supabase/services";
import { useQuery } from "@tanstack/react-query";
import ActiveRentalCard from "@/components/rentals/ActiveRentalCard";
import GroupedRentalCard from "@/components/rentals/GroupedRentalCard";
import { RentalWithRelations } from "@/types/models";

const RentalsOperator = () => {
  const [search, setSearch] = useState("");
  
  const { data: rentals = [], isLoading, error } = useQuery({
    queryKey: ['active-rentals'],
    queryFn: () => getActiveRentals(),
    refetchInterval: 60000, // Refetch every minute to update times
  });
  
  const filteredRentals = rentals.filter(
    (rental) =>
      rental.customer?.name.toLowerCase().includes(search.toLowerCase()) ||
      rental.customer?.phone.includes(search) ||
      rental.bike?.model.toLowerCase().includes(search.toLowerCase()) ||
      rental.bike?.number.toString().includes(search)
  );

  // Función para agrupar alquileres por cliente y tiempo
  const groupRentalsByCustomer = (rentals: RentalWithRelations[]) => {
    const groups: { [key: string]: RentalWithRelations[] } = {};
    const singles: RentalWithRelations[] = [];
    
    rentals.forEach(rental => {
      if (!rental.customer?.id) {
        singles.push(rental);
        return;
      }

      const startDate = new Date(rental.start_date);
      startDate.setMilliseconds(0);
      startDate.setSeconds(0);
      
      const customerId = rental.customer.id;
      const startTime = new Date(startDate).getTime();
      const groupKey = `${customerId}-${startTime}`;
      
      if (groups[groupKey]) {
        groups[groupKey].push(rental);
      } else {
        groups[groupKey] = [rental];
      }
    });
    
    // Separar grupos de alquileres múltiples vs individuales
    const multipleRentalGroups: RentalWithRelations[][] = [];
    Object.values(groups).forEach(group => {
      if (group.length > 1) {
        multipleRentalGroups.push(group);
      } else {
        singles.push(...group);
      }
    });
    
    return { multipleRentalGroups, singles };
  };

  const { multipleRentalGroups, singles } = groupRentalsByCustomer(filteredRentals);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Alquileres Activos</h1>
          <Link to="/new-rental">
            <Button>
              <BikeIcon className="mr-2 h-4 w-4" />
              Nuevo Alquiler
            </Button>
          </Link>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar alquileres..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button variant="outline" className="md:w-auto">
            <Filter className="mr-2 h-4 w-4" />
            Filtrar
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="text-gray-500">Cargando alquileres...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Error cargando los alquileres.</p>
          </div>
        ) : (multipleRentalGroups.length === 0 && singles.length === 0) ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No hay alquileres activos en este momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Renderizar grupos de alquileres múltiples */}
            {multipleRentalGroups.map((group, index) => (
              <GroupedRentalCard key={`group-${index}`} rentals={group} />
            ))}
            
            {/* Renderizar alquileres individuales */}
            {singles.map((rental) => (
              <ActiveRentalCard key={rental.id} rental={rental} />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RentalsOperator;
